import { useTranslation } from '../../../shared/i18n';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Button, Space, Tag, Descriptions, Alert, Select, message, Spin, Typography, Modal, Table,
  Popconfirm, InputNumber, Tabs, Tooltip, Progress, Row, Col,
} from 'antd';
import {
  CopyOutlined, EyeOutlined, EyeInvisibleOutlined, PlusOutlined, DeleteOutlined, ShoppingCartOutlined,
} from '@ant-design/icons';
import sslService from '../../services/sslService';
import { userPath } from '../../../shared/config/paths';
import { formatMoney } from '../../../shared/utils/money';

const { Text, Paragraph } = Typography;
const errText = (e, fb) => e?.error || e?.message || fb;

function CopyText({ text, secret = false }) {
  const { t } = useTranslation();
  const [show, setShow] = useState(!secret);
  if (!text) return <Text type="secondary">—</Text>;
  return (
    <Space size={6} style={{ maxWidth: '100%' }}>
      <code style={{ wordBreak: 'break-all' }}>{show ? text : '•'.repeat(Math.min(24, text.length))}</code>
      {secret && (show
        ? <EyeInvisibleOutlined style={{ cursor: 'pointer' }} onClick={() => setShow(false)} />
        : <EyeOutlined style={{ cursor: 'pointer' }} onClick={() => setShow(true)} />)}
      <Tooltip title={t('Copy')}>
        <CopyOutlined style={{ cursor: 'pointer', color: '#1677ff' }}
          onClick={() => { navigator.clipboard?.writeText(text); message.success(t('Copied')); }} />
      </Tooltip>
    </Space>
  );
}

function CodeBlock({ code }) {
  const { t } = useTranslation();
  return (
    <div style={{ position: 'relative' }}>
      <pre style={{ background: 'rgba(127,127,127,0.08)', padding: 12, borderRadius: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, fontSize: 12 }}>{code}</pre>
      <Button size="small" icon={<CopyOutlined />} style={{ position: 'absolute', top: 6, right: 6 }}
        onClick={() => { navigator.clipboard?.writeText(code); message.success(t('Copied')); }} />
    </div>
  );
}

function commands(a) {
  const active = (a.domains || []).filter((d) => d.status !== 'removed').map((d) => d.name);
  const names = active.length ? active : ['example.com'];
  const hasWild = names.some((n) => n.startsWith('*.'));
  const d = names.map((n) => `-d '${n}'`).join(' ');
  const certbot = [
    'certbot certonly \\',
    `  --server ${a.server_url} \\`,
    `  --eab-kid ${a.eab_kid} \\`,
    `  --eab-hmac-key ${a.eab_hmac} \\`,
    `  ${hasWild ? '--manual --preferred-challenges dns' : '--webroot -w /var/www/html'} \\`,
    `  --agree-tos -m admin@${names[0].replace(/^\*\./, '')} \\`,
    `  ${d}`,
  ].join('\n');
  const acmesh = [
    `acme.sh --register-account --server ${a.server_url} \\`,
    `  --eab-kid ${a.eab_kid} --eab-hmac-key ${a.eab_hmac}`,
    '',
    `acme.sh --issue --server ${a.server_url} \\`,
    `  ${d} \\`,
    `  ${hasWild ? '--dns dns_cf' : '--webroot /var/www/html'}`,
  ].join('\n');
  return { certbot, acmesh };
}

function BuySlotsModal({ order, acme, open, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [single, setSingle] = useState(1);
  const [wild, setWild] = useState(0);
  const [busy, setBusy] = useState(false);
  const p = acme.slot_prices || {};
  const submit = async () => {
    setBusy(true);
    try {
      const r = await sslService.acmeBuySlots(order.id, single || 0, wild || 0);
      if (r.success === false) throw r;
      const inv = r.data?.invoices?.[0];
      onClose();
      if (inv?.invoice_id) navigate(userPath(`/billing/invoices/${inv.invoice_id}`));
    } catch (e) {
      message.error(errText(e, t('Failed to place order')));
    } finally { setBusy(false); }
  };
  return (
    <Modal title={t('Buy slots')} open={open} onCancel={onClose} onOk={submit} confirmLoading={busy}
      okButtonProps={{ disabled: !(single > 0 || wild > 0) }} okText={t('Pay now')} destroyOnClose>
      <Space direction="vertical" style={{ width: '100%' }}>
        {p.single != null && (
          <Space><Text style={{ width: 96, display: 'inline-block' }}>{t('Standard domains')}</Text>
            <InputNumber min={0} max={100} value={single} onChange={setSingle} />
            <Text type="secondary">{formatMoney(p.single, p.currency)}{t('/year')}</Text></Space>
        )}
        {p.wildcard != null && (
          <Space><Text style={{ width: 96, display: 'inline-block' }}>{t('Wildcard domains')}</Text>
            <InputNumber min={0} max={100} value={wild} onChange={setWild} />
            <Text type="secondary">{formatMoney(p.wildcard, p.currency)}{t('/year')}</Text></Space>
        )}
        <Text type="secondary">{t('Prorated for the remaining days, then renews with the subscription')}</Text>
      </Space>
    </Modal>
  );
}

export default function AcmeSubscription({ order, onChanged }) {
  const { t } = useTranslation();
  const a = order.acme || {};
  const [busy, setBusy] = useState('');
  const [names, setNames] = useState([]);
  const [buyOpen, setBuyOpen] = useState(false);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try {
      const r = await fn();
      if (r?.success === false) { message.error(errText(r, t('Operation failed'))); return null; }
      if (ok) message.success(typeof ok === 'function' ? ok(r) : ok);
      return r;
    } catch (e) {
      message.error(errText(e, t('Operation failed')));
      return null;
    } finally { setBusy(''); onChanged(); }
  };

  if (order.status === 'pending') return null; // payment card is shown by the page

  const slots = a.slots || { single: { used: 0, total: 0, free: 0 }, wildcard: { used: 0, total: 0, free: 0 } };
  const cancelled = a.status === 'cancelled';
  const ready = a.status === 'active' && a.eab_kid;
  const addDomains = () => run('add', () => sslService.acmeAddDomains(order.id, names), t('Added'))
    .then((r) => { if (r) setNames([]); });

  const slotBar = (label, s) => (
    <Col xs={24} md={12}>
      <Text>{label}</Text>
      <Space style={{ width: '100%' }}>
        <Progress percent={s.total ? Math.round((s.used / s.total) * 100) : 0} showInfo={false} style={{ width: 160, margin: 0 }} />
        <Text>{s.used} / {s.total}</Text>
      </Space>
    </Col>
  );

  const addBox = (
    <Space.Compact style={{ width: '100%', maxWidth: 640 }}>
      <Select mode="tags" value={names} onChange={setNames} open={false} tokenSeparators={[',', ' ', '\n']}
        placeholder={t('example.com; *.example.com is a wildcard')} style={{ width: '100%' }} />
      <Button type="primary" icon={<PlusOutlined />} loading={busy === 'add'} disabled={!names.length} onClick={addDomains}>{t('Add')}</Button>
    </Space.Compact>
  );

  return (
    <>
      {a.last_error && !cancelled && <Alert type="error" showIcon style={{ marginBottom: 16 }} message={a.last_error} />}

      {(a.status === 'awaiting' || !a.item_id) && !cancelled ? (
        <Card title={t('Add domain')} style={{ marginBottom: 16 }}>
          <Paragraph type="secondary" style={{ marginTop: 0 }}>
            {t('{{s}} standard and {{w}} wildcard slots left', { s: slots.single.free, w: slots.wildcard.free })}
          </Paragraph>
          {addBox}
        </Card>
      ) : !ready && !cancelled ? (
        <Card style={{ marginBottom: 16 }}>
          <Alert type="info" showIcon icon={<Spin size="small" />} message={t('Setting up your ACME account, usually 5–30 seconds')}
            action={<Button size="small" loading={busy === 'refresh'} onClick={() => run('refresh', () => sslService.acmeRefresh(order.id))}>{t('Refresh')}</Button>} />
        </Card>
      ) : null}

      {ready && (
        <Card title={t('ACME account')} style={{ marginBottom: 16 }}>
          <Descriptions size="small" column={1} labelStyle={{ width: 140 }}>
            <Descriptions.Item label={t('Server URL')}><CopyText text={a.server_url} /></Descriptions.Item>
            <Descriptions.Item label="EAB KID"><CopyText text={a.eab_kid} /></Descriptions.Item>
            <Descriptions.Item label={<Tooltip title={t('Keep this secret')}>EAB HMAC Key</Tooltip>}><CopyText text={a.eab_hmac} secret /></Descriptions.Item>
            <Descriptions.Item label={t('Account ID')}><CopyText text={a.account_id} /></Descriptions.Item>
          </Descriptions>
          {!cancelled && (() => {
            const c = commands(a);
            return (
              <Tabs size="small" style={{ marginTop: 8 }} items={[
                { key: 'certbot', label: 'certbot', children: <CodeBlock code={c.certbot} /> },
                { key: 'acmesh', label: 'acme.sh', children: <CodeBlock code={c.acmesh} /> },
              ]} />
            );
          })()}
        </Card>
      )}

      {a.item_id && (
        <Card title={t('Domains')} style={{ marginBottom: 16 }}
          extra={!cancelled && <Button icon={<ShoppingCartOutlined />} onClick={() => setBuyOpen(true)}>{t('Buy slots')}</Button>}>
          <Row gutter={[16, 8]} style={{ marginBottom: 12 }}>
            {slotBar(t('Standard domains'), slots.single)}
            {slotBar(t('Wildcard domains'), slots.wildcard)}
          </Row>
          {!cancelled && <div style={{ marginBottom: 12 }}>{addBox}</div>}
          <Table size="small" pagination={false} rowKey={(r, i) => `${r.name}-${i}`} scroll={{ x: true }}
            dataSource={(a.domains || []).filter((d) => d.status !== 'removed')}
            columns={[
              { title: t('Domains'), dataIndex: 'name' },
              { title: t('Type'), dataIndex: 'type', width: 100, render: (v) => <Tag>{v === 'wildcard' ? t('Wildcard') : t('Standard')}</Tag> },
              { title: t('Status'), dataIndex: 'ca_status', width: 100, render: (v) => <Tag color={v && /valid|active|issued/i.test(v) ? 'success' : 'default'}>{v || t('Added')}</Tag> },
              { title: '', width: 60, align: 'right', render: (_, r) => !cancelled && (
                <Popconfirm okButtonProps={{ danger: true }} okText={t('Delete')}
                  title={r.free_remove ? t('Removed within 30 days: slot freed') : t('Added over 30 days ago: slot stays used this year')}
                  onConfirm={() => run('rm', () => sslService.acmeRemoveDomain(order.id, r.name), t('Deleted'))}>
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>) },
            ]} />
        </Card>
      )}

      <Card size="small">
        <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space wrap>
            {a.paid_until && <Text type="secondary">{t('Expires')}: {String(a.paid_until).slice(0, 10)}</Text>}
            {cancelled ? <Tag>{t('Canceled')}</Tag>
              : a.auto_renew ? <Tag color="green">{t('Auto-renewal')}</Tag> : <Tag>{t('Not renewing')}</Tag>}
          </Space>
          {!cancelled && (
            <Space wrap>
              {a.auto_renew ? (
                <Popconfirm title={t('The subscription ends at expiry unless you turn it back on')} okText={t('Close')}
                  onConfirm={() => run('stop', () => sslService.acmeStopAutoRenew(order.id), t('Closed'))}>
                  <Button loading={busy === 'stop'}>{t('Turn off auto-renew')}</Button>
                </Popconfirm>
              ) : a.subscription_status === 'active' && (
                <Button loading={busy === 'resume'} onClick={() => run('resume', () => sslService.acmeResumeAutoRenew(order.id), t('Auto-renewal is on'))}>
                  {t('Turn on auto-renew')}
                </Button>
              )}
              {a.can_cancel && (
                <Popconfirm title={t('Your ACME account stops working at once; the fee is refunded to your balance')} okText={t('Cancel subscription')} okButtonProps={{ danger: true }}
                  onConfirm={() => run('cancel', () => sslService.acmeCancel(order.id), (r) => (r?.refund?.amount > 0
                    ? t('Refunded {{amount}} to your balance', { amount: formatMoney(r.refund.amount, r.refund.currency, { original: true }) })
                    : r?.refund?.pending ? t('Cancelled. The refund arrives after review') : t('Canceled')))}>
                  <Tooltip title={a.refund_until ? t('Refundable until {{date}}', { date: String(a.refund_until).slice(0, 10) }) : ''}>
                    <Button danger loading={busy === 'cancel'}>{t('Cancel subscription')}</Button>
                  </Tooltip>
                </Popconfirm>
              )}
            </Space>
          )}
        </Space>
      </Card>

      <BuySlotsModal order={order} acme={a} open={buyOpen} onClose={() => setBuyOpen(false)} />
    </>
  );
}
